/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import {
    IHelp,
    IOrderSummary,
    IPaymentMethods
} from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common';
import {
    IGroup,
    IGroupDelivery,
    IGroups
} from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-groups';
import { IOrderInformation } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-order-information';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/../../common/get-sales-line';
import { IOrderConfirmationViewProps } from '@msdyn365-commerce-modules/order-management/src/modules/order-confirmation/./order-confirmation';
import { IOrderConfirmationData } from './order-confirmation.data';
import {
    checkDocumentAvailibility,
    checkZipDocument,
    renderDownloadDocumentButton,
    setLineDocumentState,
    _downloadDocument
} from '../utilities/download-document-util';
import _renderSalesLinesAddress from '../utilities/render-saleslines-address';
import { IOrderConfirmationProps } from '../definition-extensions/order-confirmation.ext.props.autogenerated';
import { downloadAsync } from '../../../customProxy/retailExtension/DataActionExtension.g';
import { Customer, IContext } from '@msdyn365-commerce/retail-proxy';
import { IDownloadDocument, ILcsabServiceAppeal, IOrder, IServiceAppeal } from '../../../customProxy/retailExtension/DataServiceEntities.g';
import { nonDownloadableProperty, PropertyType } from '../utilities/propertyTypeEnum';
import { getUrlSync } from '@msdyn365-commerce/core';
import { Button } from '@msdyn365-commerce-modules/utilities';
import moment from 'moment';
import {
    IAddress,
    IAllDocuments,
    ICustomData,
    ILCSABFormValues,
    ILegalDescription,
    ILegalDescriptionCondo,
    ILineDocument,
    IOrderSummaryExtended,
    IPropertyTypeLabels,
    IDocumentAvailibility,
    ISDABForm
} from '../utilities/types/order';
import { v4 as uuid } from 'uuid';
import { StatusTypes } from '../utilities/types/buybox';
const NUMBER_OF_ITEMS = 'ITEM_NUMBERS';
const REQUEST_SENT = 'REQUEST_SENT';

interface IRequestList {
    channelRequestId: string;
    dateRequested: string;
}
export const OrderConfirmationOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    receiptEmail,
    createdDate,
    channelReferenceId
}) => (
    <Node {...orderInformationProps}>
        {createdDate}
        {channelReferenceId}
        {receiptEmail}
    </Node>
);

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery> = ({
    deliveryProps,
    //heading,
    count,
    processing,
    address,
    trackingInfo,
    pickupDateTimeslot
}) => (
    <Node {...deliveryProps}>
        {/* {heading} */}
        {address}
        {pickupDateTimeslot}
    </Node>
);

export const OrderConfirmationGroup: React.FC<IGroup & ICustomData> = ({
    groupProps,
    delivery,
    salesLinesProps,
    salesLines,
    context,
    props,
    lineDocument,
    checkDateStatus,
    futureDate,
    retry,
    noOfRetries,
    maxRetries
}) => {
    const { downloadLabel, orderExpireStatus, fetchTimeoutLabel, fetchRetryLabel, downloadProcessingText } = props!.resources;

    const propertyTypeLabels: IPropertyTypeLabels = { ...props!.resources };

    return (
        <Node {...groupProps}>
            {/* {delivery && <OrderConfirmationGroupDelivery {...delivery} />} */}
            {delivery && delivery.shippingItemsToYou}
            {salesLines && (
                <Node {...salesLinesProps}>
                    {salesLines.map((salesLine: ISalesLine, index: number) => (
                        <div className='ms-order-confirmation__sales-lines-item'>
                            <Node {...salesLine.salesLineProps}>
                                <div className='ms-order-confirmation__product-image-section'>
                                    <img src={salesLine.data.product?.PrimaryImageUrl} alt={salesLine.data.product?.Name} />
                                    <div className='ms-order-confirmation__product-details'>
                                        <span>{salesLine.data.product?.Name}</span>
                                        <span>
                                            {_renderSalesLinesAddress(
                                                [...salesLine.data.salesLine.AttributeValues!],
                                                context!,
                                                propertyTypeLabels
                                            )}
                                        </span>
                                    </div>
                                </div>
                                <div className='ms-order-confirmation__product-price-section'>
                                    <span className='download-btn-box'>
                                        {checkDateStatus ? (
                                            renderDownloadDocumentButton(
                                                [...salesLine.data.salesLine.AttributeValues!],
                                                context,
                                                salesLine.data.product?.Name!,
                                                downloadLabel,
                                                [...lineDocument],
                                                fetchTimeoutLabel,
                                                fetchRetryLabel,
                                                downloadProcessingText,
                                                retry,
                                                noOfRetries,
                                                maxRetries,
                                            )
                                        ) : (
                                            <p>
                                                {orderExpireStatus} {futureDate.format('MMM DD, YYYY')}
                                            </p>
                                        )}
                                    </span>
                                    <span>{context?.cultureFormatter.formatCurrency(+salesLine.data.salesLine.Price!)}</span>
                                </div>
                            </Node>
                        </div>
                    ))}
                </Node>
            )}
        </Node>
    );
};

export const OrderConfirmationGroups: React.FC<IGroups & ICustomData> = ({
    groupsProps,
    groups,
    context,
    props,
    lineDocument,
    checkDateStatus,
    futureDate,
    retry,
    noOfRetries,
    maxRetries
}) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderConfirmationGroup
                key={index}
                lineDocument={[...lineDocument]}
                context={context}
                props={props}
                {...group}
                checkDateStatus={checkDateStatus}
                futureDate={futureDate}
                retry={retry}
                noOfRetries={noOfRetries}
                maxRetries={maxRetries}
            />
        ))}
    </Node>
);

export const OrderConfirmationOrderSummary: React.FC<IOrderSummary & IOrderSummaryExtended> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    salesOrder,
    earnedPoints,
    resources
}) => {
    const { itemsInCurrentOrderLabel } = resources;
    return (
        <Node {...orderSummaryProps}>
            {heading}
            {
                <Node tag='p' className='ms-order-confirmation__item-count'>
                    {itemsInCurrentOrderLabel}
                    {salesOrder?.SalesLines?.length}
                </Node>
            }
            {subtotal}
            {shipping}
            {tax}
            {totalAmount}
            {earnedPoints}
        </Node>
    );
};

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderConfirmationView: React.FC<IOrderConfirmationViewProps & IOrderConfirmationProps<IOrderConfirmationData>> = props => {
    const { moduleProps, alert, loading, orderInfomation, orderSummary, groups, context, resources, salesOrder } = props;
    const {
        orderValidStatus,
        orderExpireStatus,
        fetchTimeoutLabel,
        fetchRetryLabel,
        downloadProcessingText,
        downloadAllLabel,
        propertyTaxCertProductType,
        sdabFormName,
        lcsabFormName
    } = resources;
    const referenceId = props.data.orderHydration.result?.salesOrder.ChannelReferenceId;
    const { config } = context?.request.app;
    const { user } = context.request;
    const { isAuthenticated } = user;
    const { fileExpirationDays, maxRetries, documentsTimeOutDuration } = config;
    const orderCreatedDate = moment(salesOrder?.CreatedDateTime).format(moment.HTML5_FMT.DATE);
    const futureDate = moment(moment(orderCreatedDate).add(fileExpirationDays, 'days'));
    const todayDate = moment(moment(new Date()).format(moment.HTML5_FMT.DATE));
    const checkDateStatus = futureDate > todayDate;
    const [lineDocument, setLineDocument] = React.useState<ILineDocument[]>([]);
    const [noOfRetries, setNoOfRetries] = React.useState<number>(0);
    const [allDocuments, setAllDocuments] = React.useState<IAllDocuments>({ isAvailable: false, zipStatus: true });
    const timeout = React.useRef<NodeJS.Timeout>();
    const intervals = React.useRef<{ zip?: NodeJS.Timer; doc?: NodeJS.Timer }>({});
    const [barWidth, setBarWidth] = React.useState(1);
    const [corelationId] = React.useState(uuid());
    const [errorText, setErrorText] = React.useState<string>();
    const { orderPlaceSuccessfullyText, viewOrderHistoryButtonText } = props.resources;
    const storageName = `${props.context.request.user.customerAccountNumber}-${NUMBER_OF_ITEMS}`;
    const requestStorageName = `${props.context.request.user.customerAccountNumber}-${REQUEST_SENT}`;
    // function is to use CRT API for document generation
    const mapSdabDataToAppealService = (sdabData: ISDABForm | undefined) => {
        const appealService: IServiceAppeal = { CustomerOrderId: 0, TransactionDate: new Date() };
        appealService.CustomerInfo_Address1 = sdabData?.customerInformation?.address1;
        appealService.CustomerInfo_Address2 = sdabData?.customerInformation?.address2;
        appealService.CustomerInfo_City = sdabData?.customerInformation?.city;
        appealService.CustomerInfo_Province = sdabData?.customerInformation?.province;
        appealService.CustomerInfo_Country = sdabData?.customerInformation?.country;
        appealService.CustomerInfo_EmailAddress = sdabData?.customerInformation?.email + '|' + sdabData?.customerInformation?.productOwnerEmail;
        appealService.CustomerInfo_LastName = sdabData?.customerInformation?.lastName;
        appealService.CustomerInfo_FirstName = sdabData?.customerInformation?.firstName;
        appealService.CustomerInfo_ResidentialPhone = sdabData?.customerInformation?.residentialPhone;
        appealService.CustomerInfo_PostalCode = sdabData?.customerInformation?.postalCode;
        // Appalent Information Section
        appealService.AppellantInfo_AgentName = sdabData?.appelantInformation?.agentName;
        appealService.AppellantInfo_City = sdabData?.appelantInformation?.city;
        appealService.AppellantInfo_FirstName = sdabData?.appelantInformation?.firstName;
        appealService.AppellantInfo_LastName = sdabData?.appelantInformation?.lastName;
        appealService.AppellantInfo_PostalCode = sdabData?.appelantInformation?.postalCode;
        appealService.AppellantInfo_Province = sdabData?.appelantInformation?.province;
        appealService.AppellantInfo_StreetAddress1 = sdabData?.appelantInformation?.address1!;
        appealService.AppellantInfo_StreetAddress2 = sdabData?.appelantInformation?.address2!;
        appealService.AppellantInfo_ResidentialPhone = sdabData?.appelantInformation?.residentialPhone;
        appealService.AppellantInfo_EmailAddress = sdabData?.appelantInformation?.email;
        appealService.SiteInfo_MunicipalAddressofSite = sdabData?.customerInformation?.municipalAddress;
        appealService.SiteInfo_DevelopmentPermit_SubdivisionApplication_FileNumber = sdabData?.customerInformation?.otherDocuments;
        appealService.UsingAnAgentOrLegalCounsel = sdabData?.appealAgainst?.usingAgentOrLegalCounsel;
        appealService.EstimatedPresentationTime = sdabData?.appealAgainst?.presentationTime;
        // TODO:
        // appealService.AppealAgainst = sdabData?.appealAgainst?.appeal;
        appealService.AppealAgainstDevelopment = sdabData?.appealAgainst?.appealAgainstDevelopment;
        appealService.AppealAgainstSubDivisionApplication = sdabData?.appealAgainst?.appealAgainstSubDivisionApplication;
        appealService.AppealAgainstNoticOrder = sdabData?.appealAgainst?.appealAgainstNoticOrder;
        appealService.ReasonForAppeal = sdabData?.appealAgainst?.reasonOfAppeal;
        appealService.AnticipatePreliminaryIssuesWithAppeal = sdabData?.anticipateIssues?.isAnticipateIssuesWithYourAppeal;
        appealService.PreliminaryIssuesWithAppeal = sdabData?.anticipateIssues?.isAnticipateIssuesWithYourAppealDescription;
        appealService.NumberOfWitnessesExpertsToHearing = sdabData?.witness?.howManyWitness;
        appealService.AnticipateWitnessesExpertsToHearing = sdabData?.witness?.bringingWitness;
        // Need to override the TransactionDate and CustomerOrderId
        return appealService;
    };
    
    React.useEffect(() => {
        // Adding lines to state varibale to keep track of documents.
        setLineDocumentState(groups, context, lineDocument, setLineDocument);
    }, [groups?.groups.length, groups?.groups[0].salesLines.length]);

    const requestPendingDocs = async () => {
        const ctx: IContext = { callerContext: context.actionContext };
        const customerDetails: Customer = props.data.customerInformation.result!;
        let orderRequest: IOrder = {
            Id: 0,
            CorrelationId: corelationId,
            DownloadDocuments: []
        };
        // getting the lines from the state and creating a request
        lineDocument
            .filter(doc => !doc.isSet).forEach(line => {
            const downloadDocument: IDownloadDocument = {
                Id: 0,
                ProductId: parseInt(line.productID!, 10),
                ProductType: line.productType,
                ProductName: line.productName,
                Folio: line.folioNumber || ''
            };
            switch (line.propertyType) {
                // TODO: Ensure correct criteria for default
                case PropertyType.default:
                    downloadDocument.SearchCriteria = {
                        QueryType: line.propertyType,
                        DocumentId: 0
                    };
                    const folioArr = line.folioNumber?.split('||');
                    const value = folioArr && (folioArr?.length > 1 ? folioArr[1] : folioArr[0]);
                    downloadDocument.Folio = value;
                    break;
                case PropertyType.rollNumber:
                    downloadDocument.SearchCriteria = {
                        QueryType: line.propertyType,
                        RollNumber: line.lookFor,
                        DocumentId: 0
                    };
                    break;
                case PropertyType.lincNumber:
                    downloadDocument.SearchCriteria = {
                        QueryType: line.propertyType,
                        LincNumber: line.lookFor,
                        DocumentId: 0
                    };
                    break;
                case PropertyType.titleNumber:
                    downloadDocument.SearchCriteria = {
                        QueryType: line.propertyType,
                        TitleNumber: line.lookFor,
                        DocumentId: 0
                    };
                    break;
                case PropertyType.cpid:
                    downloadDocument.SearchCriteria = {
                        QueryType: line.propertyType,
                        CPID: line.lookFor,
                        DocumentId: 0
                    };
                    break;
                // below all cases have same case
                case PropertyType.community:
                case PropertyType.ats:
                case PropertyType.cityWide:
                case PropertyType.none:
                case PropertyType.quadrant:
                case PropertyType.blockProfile:
                    downloadDocument.SearchCriteria = {
                        QueryType: line.propertyType,
                        DocumentId: parseInt(line.documentId!)
                    };
                    break;
                case PropertyType.address:
                    const address: IAddress | undefined = { ...line.address };
                    downloadDocument.SearchCriteria = {
                        QueryType: line.propertyType,
                        DocumentId: 0,
                        Address: {
                            Suite: address?.suite ? address?.suite : '',
                            Number: address?.number!,
                            StreetName: address?.streetName!,
                            StreetType: address?.streetType!,
                            Quadrant: address?.quadrant!
                        }
                    };
                    break;
                case PropertyType.legalDescription:
                    const legalDesc: ILegalDescription | undefined = { ...line.legalDesc };
                    downloadDocument.SearchCriteria = {
                        QueryType: line.propertyType,
                        LegalDescription: {
                            Plan: legalDesc.plan,
                            Block: legalDesc?.block ? legalDesc?.block : '',
                            Lot: legalDesc?.lot ? legalDesc?.lot : ''
                        },
                        DocumentId: 0
                    };
                    break;
                case PropertyType.legalDescriptionCondo:
                    const legalDescCondo: ILegalDescriptionCondo | undefined = { ...line.legalDescCondo };
                    downloadDocument.SearchCriteria = {
                        QueryType: line.propertyType,
                        LegalDescriptionCondo: {
                            Plan: legalDescCondo.plan,
                            Unit: legalDescCondo?.unit ? legalDescCondo?.unit : ''
                        },
                        DocumentId: 0
                    };
                    break;
                case PropertyType.lcsab:
                    const lcsabData: ILCSABFormValues | undefined = { ...line.lcsab };
                    // Now we've to map lcsabData into IServiceAppeal Address (CRT Interface)
                    const lcsabAppealService: ILcsabServiceAppeal = mapLCSABDataToLCSABAppealService(lcsabData);
                    lcsabAppealService.TransactionDate = salesOrder?.CreatedDateTime!;
                    lcsabAppealService.CustomerOrderId = 0;
                    lcsabAppealService.ProductNm = line.productName;
                    lcsabAppealService.ProductId = line.productID;
                    lcsabAppealService.FormNm = lcsabFormName;
                    lcsabAppealService.CustomerOrderNum = salesOrder?.ChannelReferenceId!;
                    downloadDocument.LcsabServiceAppeal = lcsabAppealService;
                    (downloadDocument.Folio = line.folioNumber),
                        (downloadDocument.ProductType = line.productType),
                        (downloadDocument.SearchCriteria = {
                            QueryType: line.propertyType,
                            RollNumber: '',
                            DocumentId: 0
                        });
                    break;
                case PropertyType.sdab:
                    const sdabData: ISDABForm | undefined = { ...line.sdab };
                    // Now we've to map sdabData into IServiceAppeal Address (CRT Interface)
                    const appealServiceData: IServiceAppeal = mapSdabDataToAppealService(sdabData);
                    appealServiceData.TransactionDate = salesOrder?.CreatedDateTime!;
                    appealServiceData.CustomerOrderId = 0;
                    appealServiceData.ProductNm = line.productName;
                    appealServiceData.ProductId = line.productID;
                    appealServiceData.FormNm = sdabFormName;
                    appealServiceData.CustomerOrderNum = salesOrder?.ChannelReferenceId!;
                    downloadDocument.ServiceAppeal = appealServiceData;
                    // downloadDocument.ServiceAppeal.ProductId
                    (downloadDocument.Folio = line.folioNumber),
                        (downloadDocument.ProductType = line.productType),
                        (downloadDocument.SearchCriteria = {
                            QueryType: line.propertyType,
                            RollNumber: '',
                            DocumentId: 0
                        });
                    break;
                default:
                    break;
            }
            const tenderTypesCheck = props.data.tenderTypes.result && props.data.tenderTypes.result?.length > 0;
            if (
                downloadDocument &&
                downloadDocument.SearchCriteria &&
                tenderTypesCheck &&
                customerDetails &&
                user.isAuthenticated &&
                line.productType === propertyTaxCertProductType
            ) {
                downloadDocument.SearchCriteria.PropertyTaxInformation = {
                    Attention: customerDetails?.Name || '',
                    CompanyId: customerDetails?.InvoiceAccount || '',
                    ContactId: customerDetails?.AccountNumber || '',
                    Email: customerDetails?.Email || '',
                    Fax: '',
                    LoginId: customerDetails?.InvoiceAccount || '',
                    PaymentCode: props.data.tenderTypes.result && props.data.tenderTypes.result[0].Name,
                    PhoneNumber: customerDetails?.Phone || '',
                    RateId: line.rateId || 0
                };
            }
            orderRequest.DownloadDocuments?.push({ ...downloadDocument });
        });
        try {
           
            const orderRequestForDownload = { ...orderRequest };
            const downloadedDocument = orderRequest.DownloadDocuments;
      
            orderRequestForDownload.DownloadDocuments = [];
            downloadedDocument && downloadedDocument.forEach((line: IDownloadDocument) => {
                const isDuplicate = orderRequestForDownload?.DownloadDocuments?.some(
                    (doc: IDownloadDocument) => JSON.stringify(doc) === JSON.stringify(line)
                );
                if (!isDuplicate) {
                    orderRequestForDownload?.DownloadDocuments?.push({ ...line });
                }
            }
            );

            
            // requesting CRT API
            await downloadAsync(ctx, referenceId!, { ...orderRequestForDownload }, user?.emailAddress || '', user?.userClaims?.companyName || '');
            const requestArr: IRequestList[] = JSON.parse(localStorage.getItem(requestStorageName)!) || [];
            const todayDate = moment(moment().format(moment.HTML5_FMT.DATE));
            const validRequest: IRequestList[] = requestArr.filter((item: IRequestList) => {
                const requestDate = moment(item.dateRequested).format(moment.HTML5_FMT.DATE);
                const requestDateAdded = moment(requestDate).add(5, 'days');
                return requestDateAdded > todayDate;
            });
            validRequest.push({
                dateRequested: moment().format(moment.HTML5_FMT.DATE),
                channelRequestId: referenceId!
            });
            localStorage?.setItem(requestStorageName, JSON.stringify(validRequest));
        } catch (e) {
            const error = e as any;
            const status = error.status;
            if (status === 'Service Unavailable') {
                setErrorText(`${resources.serverErrorCustom} ${StatusTypes.Canceled} : ${orderRequest.CorrelationId}`);
            } else {
                setErrorText(`${resources.serverErrorCustom} ${StatusTypes.CRTError} : ${orderRequest.CorrelationId}`);
            }
            
            setAllDocuments(prevSt => ({
                ...prevSt,
                isAvailable: false,
                zipStatus: false,
                zipDocumentSecureURL: ''
            }));
            setLineDocument(lineDocument.map(doc => ({ ...doc, isTimedOut: true })));
            context.telemetry.error(`Document generation failed ${e}`);
        }
    };
    React.useEffect(() => {
        let bar = document.getElementById('myBar');
        if (bar) {
            bar.style.width = barWidth + '%';
        }
    }, [barWidth]);

  
    
    React.useEffect(() => {
        // Adding lines to state varibale to keep track of documents.
        const setLine = async () => {
            await setLineDocumentState(groups, context, lineDocument, setLineDocument);
        };
        setLine();
    }, [groups?.groups.length, groups?.groups[0].salesLines.length]);

    const startURLFetchInterval = () => {
        if (
            checkDateStatus &&
            lineDocument.some(doc => doc.productType !== PropertyType.sdab.toString() && doc.productType !== PropertyType.lcsab.toString())
        ) {
            clearLeaks();
            const zip = checkZipDocument(allDocuments, referenceId!, setAllDocuments, context, setBarWidth, downloadableFiles);
            const docs: IDocumentAvailibility = {
                groups,
                lineDocument,
                allDocuments,
                setLineDocument,
                context,
                channelReferenceId: referenceId!
            };
            const doc = checkDocumentAvailibility({ ...docs });
            const newTimeout = setTimeout(() => {
                clearInterval(zip);
                clearInterval(doc);                
                setAllDocuments(prev => ({ ...prev, isError: lineDocument.some(doc => !doc.isSet && lineDocument.every(line => line.documentName === doc.documentName && !line.isSet)) }));
                setLineDocument(lineDocument.map(doc => ({ ...doc, isError: !doc.isSet })));
            }, documentsTimeOutDuration);
            intervals.current = { zip, doc };
            timeout.current = newTimeout;
        }
    }
    const clearLeaks = () => {
        timeout.current && clearTimeout(timeout.current);
        Object.values(intervals.current).forEach(interval => interval && clearInterval(interval));
    };
    const retry = () => {
        if (noOfRetries >= (maxRetries ?? 2)) {
            return;
        }
        clearLeaks();
        setNoOfRetries(prev => prev + 1);
        setAllDocuments({ isAvailable: false, isError: false });
        setLineDocument(prev => prev.map(doc => ({ ...doc, isError: false })));
        requestPendingDocs().then(startURLFetchInterval);
    };
    // only execute when tender type and customer information is available
  
    // Calling Azure download all file again and again untill it's not available
    React.useEffect(() => {
        allDocuments.isAvailable && intervals.current.zip && clearInterval(intervals.current.zip);
        if (!allDocuments?.zipStatus) {
            setLineDocument(lineDocument.map(doc => ({ ...doc, isTimedOut: true })));
        }
    }, [allDocuments.isAvailable, allDocuments?.zipStatus]);
    const downloadableFiles = lineDocument.filter(line => !(line.propertyType! in nonDownloadableProperty)).length;
    React.useEffect(() => {
        if (lineDocument.length) {
            // lineDocument[0].isSet = false;
            
            lineDocument.some(doc => !doc.isSet) || clearLeaks();
        }
    }, [lineDocument.length, allDocuments.zipStatus]);

    React.useEffect( () => {
        const sendReq = async () => {
            const numberofItems: number = parseInt(JSON.parse(localStorage.getItem(storageName)!) || 0);
            const requestArr: IRequestList[] = JSON.parse(localStorage.getItem(requestStorageName)!) || [];
            const isRequestSent = requestArr.findIndex(x => x.channelRequestId === referenceId) > -1;
            const xxddd = numberofItems === lineDocument.length
            // !isRequestSent && xxddd ? requestPendingDocs() : null;
            if (lineDocument.length && (!isRequestSent && xxddd)) {
                if (lineDocument.findIndex(x => x.productType === propertyTaxCertProductType) > -1) {
                    if (props.data.tenderTypes.result && props.data.customerInformation.result) {
                        await requestPendingDocs();
                    }
                } else {
                    await requestPendingDocs();
                }
            }
        }
        sendReq().then(startURLFetchInterval)
        return clearLeaks();
    }, [lineDocument.length, props.data.tenderTypes.result, props.data.customerInformation.result]);
            // const interval = checkDocumentAvailibility({ ...docs });
            // const timeout = setTimeout(() => {
            //     clearInterval(interval);
            //     setLineDocument(lineDocument.map(doc => ({ ...doc, isTimedOut: !doc.isSet })));
            // }, documentsTimeOutDuration);
            // return () => {
            //     clearInterval(interval);
            //     clearTimeout(timeout);
            // };

    const backToShoppingUrl = getUrlSync('orderHistory', props.context.actionContext) || '';
    const viewOrderHistoryButton = (
        <Button className='cui utility-btn plain' href={backToShoppingUrl} title={viewOrderHistoryButtonText}>
            {viewOrderHistoryButtonText}
        </Button>
    );

    return (
        <Module {...moduleProps}>
            <div>
                <div className='cui alert-banner inline success'>
                    <div className='alert-body'>
                        <span className='alert-icon cicon-check-circle'></span>
                        <p>{orderPlaceSuccessfullyText}</p>
                        {isAuthenticated && viewOrderHistoryButton}
                    </div>
                </div>
            </div>
            <div className='summary-head'>{orderInfomation && <OrderConfirmationOrderInfomation {...orderInfomation} />}</div>
            {alert}
            {loading}

            {!downloadableFiles ? null : (
                <div>
                    <div className='top-btn-box'>
                        {/* Download all files button */}
                        {checkDateStatus ? (
                            <p>
                                {orderValidStatus} {futureDate.format('MMM DD, YYYY')}
                            </p>
                        ) : (
                            <p>
                                {orderExpireStatus} {futureDate.format('MMM DD, YYYY')}
                            </p>
                        )}
                        {checkDateStatus &&
                            (allDocuments.isError ? (
                                <button
                                    className={`App-link ms-order-confirmation__btn-download-all cui primary ${!allDocuments.isAvailable &&
                                    'processing-btn'}`}
                                    disabled={noOfRetries! >= (maxRetries ?? 2)}
                                    onClick={retry}
                                >
                                {noOfRetries! < (maxRetries ?? 2) ? fetchRetryLabel : fetchTimeoutLabel}
                                </button>
                            ) : (
                                <button
                                    className={`App-link ms-order-confirmation__btn-download-all cui primary ${!allDocuments.isAvailable &&
                                        'processing-btn'}`}
                                    onClick={() => _downloadDocument(context, `${referenceId!}`, allDocuments.zipDocumentSecureURL)}
                                    disabled={!allDocuments.isAvailable}
                                >
                                    {!allDocuments.isAvailable ? (
                                        <>
                                            <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>{' '}
                                            <span>{downloadProcessingText}</span>
                                        </>
                                    ) : (
                                        downloadAllLabel
                                    )}
                                </button>
                            ))}
                    </div>
                    {errorText && (
                        <div aria-live='assertive' aria-label={errorText} role='alert' className='cui alert-small error'>
                            {errorText}
                        </div>
                    )}
                </div>
            )}
            <div className='order-confirmation-wrap'>
                <div className='product-item-box'>
                    {groups && (
                        <OrderConfirmationGroups
                            lineDocument={[...lineDocument]}
                            context={context}
                            props={props}
                            {...groups}
                            checkDateStatus={checkDateStatus}
                            futureDate={futureDate}
                            retry={retry}
                            noOfRetries={noOfRetries}
                            maxRetries={maxRetries}
                        />
                    )}
                </div>
                <div className='order-summary-box'>
                    {orderSummary && <OrderConfirmationOrderSummary {...orderSummary} salesOrder={salesOrder} resources={resources} />}
                </div>
            </div>
        </Module>
    );
};

function mapLCSABDataToLCSABAppealService(lcsabData: ILCSABFormValues): ILcsabServiceAppeal {
    const appealService: ILcsabServiceAppeal = { CustomerOrderId: 0, TransactionDate: new Date() };
    appealService.CustomerInfo_FirstName = lcsabData?.customerInformation?.firstName;
    appealService.CustomerInfo_LastName = lcsabData?.customerInformation?.lastName;
    appealService.CustomerInfo_EmailAddress = lcsabData?.customerInformation?.email + '|' + lcsabData?.customerInformation?.productOwnerEmail;
    appealService.CustomerInfo_Address1 = lcsabData?.customerInformation?.address1;
    appealService.CustomerInfo_Address2 = lcsabData?.customerInformation?.address2;
    appealService.CustomerInfo_City = lcsabData?.customerInformation?.city;
    appealService.CustomerInfo_Province = lcsabData?.customerInformation?.province;
    appealService.CustomerInfo_PostalCode = lcsabData?.customerInformation?.postalCode;
    appealService.CustomerInfoDaytimePhoneNumber = lcsabData?.customerInformation?.dayTimePhoneNumber;
    appealService.CustomerInfoSecondaryPhone = lcsabData?.customerInformation?.secondaryPhoneNumber;
    // Appalent Information Section
    appealService.AppellantInfo_FirstName = lcsabData?.appellantInformation?.firstName;
    appealService.AppellantInfo_LastName = lcsabData?.appellantInformation?.lastName;
    appealService.AppellantInfo_EmailAddress = lcsabData?.appellantInformation?.email;
    appealService.AppellantInfoAddress1 = lcsabData?.appellantInformation?.address1;
    appealService.AppellantInfoAddress2 = lcsabData?.appellantInformation?.address2;
    appealService.AppellantInfo_PostalCode = lcsabData?.appellantInformation?.postalCode;
    appealService.AppellantInfo_City = lcsabData?.appellantInformation?.city;
    appealService.AppellantInfo_Province = lcsabData?.appellantInformation?.province;
    appealService.AppellantInfoSecondaryPhoneNumber = lcsabData?.appellantInformation?.secondaryPhoneNumber;
    appealService.AppellantInfoDaytimePhoneNumber = lcsabData?.appellantInformation?.dayTimePhoneNumber;
    appealService.SiteInfo_MunicipalAddressofSite = lcsabData?.appellantInformation?.municipalAddress;
    // Using agent "yes, no, unknown"
    appealService.UsingAnAgentOrLegalCounsel = lcsabData.usingAgent;
    // Agent Information
    appealService.AgentFirstName = lcsabData.agentInformation?.agentName;
    appealService.AgentEmail = lcsabData.agentInformation?.email;
    appealService.AgentCity = lcsabData.agentInformation?.city;
    appealService.AgentPostalCode = lcsabData.agentInformation?.postalCode;
    appealService.AgentProvince = lcsabData.agentInformation?.province;
    // TODO: Agent Address1 and Address2 are missing from CRT @Abdul Rehman
    // appealService.address1 = lcsabData.agentInformation?.address1;
    // appealService.address2 = lcsabData.agentInformation?.address2;
    appealService.AgentDayTimePhone = lcsabData.agentInformation?.dayTimePhoneNumber;
    appealService.AgentSecondaryPhone = lcsabData.agentInformation?.secondaryPhoneNumber;
    // Category with dashes "-"
    appealService.AppealCategory = lcsabData?.categoryAppealTypeForCRT;
    appealService.OrderDate = lcsabData.dateAndReasonForAppeal?.date;
    appealService.ReasonForAppeal = lcsabData.dateAndReasonForAppeal?.reasonForAppeal;
    // Need to override the TransactionDate and CustomerOrderId
    return appealService;
}
export default OrderConfirmationView;
